import { Injectable } from "../../../lib/web/reflection/injectable";
import { HttpClientResponse, HttpService } from "../../../lib/web/services/http.service";
import { ApiRequestOptions, IApiService } from "./iapi.service";
import { Session, SessionService } from "./session.service";

@Injectable({ type: 'singleton', token: 'API_SERVICE_TOKEN'})
export class ApiService implements IApiService {

    public constructor(private _httpService: HttpService, private _sessionService: SessionService) {
    }

    public init(): void {
    }

    public async get(url: string, data?: { headers?: any, query?: any }, options?: ApiRequestOptions): Promise<any> {
        try {
            const { headers } = data || {};
            const response: HttpClientResponse = await this._httpService.get(url, {
                ...data,
                headers: this.getApiHeaders(headers),
            });
            return this.getApiResponse(response);
        }
        catch (e: any) {
            return this.handleApiError(e, options);
        }
    }

    public async post(url: string, data?: { headers?: any, query?: any, body?: any}, options?: ApiRequestOptions): Promise<any> {
        return await this.nonQueryRequest('post', url, data, options);
    }

    public async put(url: string, data?: { headers?: any, query?: any, body?: any}, options?: ApiRequestOptions): Promise<any> {
        return await this.nonQueryRequest('put', url, data, options);
    }

    public async delete(url: string, data?: { headers?: any, query?: any, body?: any}, options?: ApiRequestOptions): Promise<any> {
        return await this.nonQueryRequest('delete', url, data, options);
    }

    public async nonQueryRequest(method: 'post' | 'put' | 'delete', url: string, data?: { headers?: any, query?: any, body?: any}, options?: ApiRequestOptions): Promise<any> {
        try {
            const { headers, body } = data || {};
            const response: HttpClientResponse = await (this._httpService as any)[method](url, {
                ...data,
                headers: this.getApiHeaders(headers),
                body: this.getApiBody(body)
            });
            return this.getApiResponse(response);
        }
        catch (e: any) {
            return this.handleApiError(e, options);
        }
    }

    private handleApiError(e: any, options?: ApiRequestOptions): null {
        const error: any = this.getApiResponse(e, true);
        if (options?.ignore?.includes(error?.statusCode)) {
            logger.warn(error);
            return null;
        }
        throw error;
    }

    private getApiResponse(response: HttpClientResponse, isError?: boolean): any {
        const { statusCode, data, headers } = response;
        try {   
            if (isError) {
                if (statusCode == 401 && (headers || {})['x-authenticate'] == 'Env') {
                    this.redirect(CONFIG.ENV_PROTECT_METHOD == 'code' ? '/e/code': '/e/sign-in');
                }
                else if (statusCode == 401) {
                    this.removeExpiredSession();
                }
                else if (statusCode == 499) {
                    this.redirect('/waiting-room');
                }
                response.data = data ? JSON.parse(data): null;
                return response;
            }
            else {
                return JSON.parse(data);
            }            
        }
        catch (e: any) {
            return response;
        }
    }

    private removeExpiredSession(): void {
        // El servidor borra la cookie active-session cuando el token de sesión no es válido;
        // si aún queda la sesión local, se limpia para que la web deje de actuar como usuario logueado
        if (!this._sessionService.isSigned && this._sessionService.get()) {
            this._sessionService.remove();
        }
    }

    private redirect(pathname: string): void {
        // Evita recargar en bucle si la petición falla desde la propia página de destino
        if (location.pathname != pathname) {
            location.assign(pathname);
        }
    }

    private getApiBody(body: any): string {
        return body ? JSON.stringify(body): null;
    }

    private getApiHeaders(headers: any): any {
        const apiHeaders: any = {                
            ...(headers || {}),
            'Content-Type': 'application/json;charset=UTF-8',
        };        
        return apiHeaders;
    }    

    public async checkSession(): Promise<void> {
        if (this._sessionService.isSigned && !this._sessionService.sessionChecked) {
            const user: any = await this.get('/api/v1/user/me', null, { ignore: [0, 401] });
            if (user) {
                this._sessionService.renewed(user);
                window.dispatchEvent(new CustomEvent('user-picture-changed'));
            }
        }
        else {
            this._sessionService.checked();
        }

        if (this._sessionService.isNewSession) {
            this._sessionService.setNewSession(false);
        }
    }
}